import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';

interface HouseParticlesProps {
    houseId: string;
}

// Helper to lighten/darken a hex color
function adjustColor(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
    const toHex = (c: number) => {
        const hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    };
    return `#${toHex(R)}${toHex(G)}${toHex(B)}`;
}

export default function HouseParticles({ houseId }: HouseParticlesProps) {
    const { getHouse } = useApp();
    const house = getHouse(houseId);

    // Generate particle config based on house colors
    const config = useMemo(() => {
        if (!house) {
            return { count: 0, colors: [], minSize: 0, maxSize: 0, speed: 0, direction: 'none', blur: '', blend: 'normal', type: 'circle' };
        }

        const baseColor = house.color;
        const accentColor = house.accent;

        // Generate a palette from house colors
        const colors = [
            adjustColor(baseColor, 40),  // Lighter
            adjustColor(baseColor, 20),
            baseColor,
            accentColor,
            adjustColor(accentColor, 30),
        ];

        // Determine Elemental Type based on House ID (assuming 1=Gryffindor, 2=Hufflepuff, 3=Ravenclaw, 4=Slytherin)
        // Or if we can't be sure, we map cyclically
        const houseIndex = parseInt(houseId.replace('house', '')) || 1;

        let particleConfig = {
            count: 30,
            colors,
            minSize: 3,
            maxSize: 8,
            speed: 1,
            direction: 'up',
            blur: 'blur-[1px]',
            blend: 'screen',
            type: 'circle' // circle, square, or custom
        };

        if (houseIndex === 1) {
            // WHITE HOUSE (Ice/Snow): Falling snow, gentle, white/blue
            particleConfig = {
                ...particleConfig,
                count: 40,
                speed: 1.0,
                direction: 'down', // Snow falls
                blend: 'screen',
                minSize: 2,
                maxSize: 6
            };
        } else if (houseIndex === 2) {
            // YELLOW HOUSE (Sun/Light): Radiating warmth, floating upwards
            particleConfig = {
                ...particleConfig,
                count: 35,
                speed: 0.5,
                direction: 'air', // Twinkle/float
                blend: 'screen',
                minSize: 4,
                maxSize: 10
            };
        } else if (houseIndex === 3) {
            // RED HOUSE (Fire): Fast ascending sparks
            particleConfig = {
                ...particleConfig,
                count: 40,
                speed: 2.5,
                direction: 'fire',
                blend: 'screen',
                maxSize: 6
            };
        } else if (houseIndex === 4) {
            // BLUE HOUSE (Water): Rising bubbles
            particleConfig = {
                ...particleConfig,
                count: 30,
                speed: 0.8,
                direction: 'water',
                blend: 'soft-light', // Watery look
                minSize: 4,
                maxSize: 12
            };
        } else if (houseIndex === 5) {
            // GREEN HOUSE (Plants): Falling leaves/pollen
            particleConfig = {
                ...particleConfig,
                count: 30,
                speed: 0.8,
                direction: 'earth', // Meandering down
                blend: 'overlay',
                minSize: 4,
                maxSize: 8
            };
        }

        return particleConfig;
    }, [house, houseId]);

    // Generate random particles based on config
    const particles = useMemo(() => {
        return [...Array(config.count)].map((_, i) => ({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            size: Math.random() * (config.maxSize - config.minSize) + config.minSize,
            color: config.colors[Math.floor(Math.random() * config.colors.length)],
            duration: (Math.random() * 2 + 3) / config.speed,
            delay: Math.random() * 5
        }));
    }, [config]);

    if (!house || config.count === 0) return null;

    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
            {particles.map((p) => {
                let animate: any = {};
                const initialStyle: React.CSSProperties = {
                    left: `${p.x}%`,
                    width: p.size,
                    height: p.size,
                    backgroundColor: p.color,
                    mixBlendMode: config.blend as React.CSSProperties['mixBlendMode']
                };

                // Define Animations by Elemental Type
                if (config.direction === 'fire') {
                    // Fire: Rise fast, wiggle slightly, fade out top
                    initialStyle.top = '110%';
                    animate = {
                        y: ['0vh', `-${Math.random() * 60 + 50}vh`],
                        x: ['0%', `${Math.random() * 20 - 10}%`],
                        opacity: [1, 0],
                        scale: [1, 0]
                    };
                } else if (config.direction === 'water') {
                    // Water: Rise slowly, wobble, variable opacity
                    initialStyle.top = '110%';
                    animate = {
                        y: ['0vh', '-120vh'],
                        x: ['0%', `${Math.random() * 10 - 5}%`, `${Math.random() * 10 - 5}%`, '0%'],
                        opacity: [0, 0.6, 0]
                    };
                } else if (config.direction === 'earth') {
                    // Earth: Fall gently, sway like leaves
                    initialStyle.top = '-10%';
                    animate = {
                        y: ['0vh', '120vh'],
                        x: ['0%', `${Math.random() * 40 - 20}%`],
                        opacity: [0, 0.8, 0],
                        rotate: [0, 360],
                    };
                } else if (config.direction === 'air') {
                    // Air: Twinkle in place, drift slightly
                    initialStyle.top = `${p.y}%`;
                    animate = {
                        scale: [0, 1.5, 0],
                        opacity: [0, 1, 0],
                        x: [0, Math.random() * 10 - 5]
                    };
                } else {
                    // Magic/Default
                    initialStyle.top = '110%';
                    animate = {
                        y: ['0vh', '-110vh'],
                        x: ['0%', `${Math.random() * 30 - 15}%`],
                        opacity: [0, 1, 0],
                        rotate: [0, 180]
                    };
                }

                return (
                    <motion.div
                        key={p.id}
                        className={`absolute rounded-full ${config.blur}`}
                        style={initialStyle}
                        animate={animate}
                        transition={{
                            duration: p.duration,
                            repeat: Infinity,
                            delay: p.delay,
                            ease: config.direction === 'fire' ? 'easeOut' : 'linear',
                        }}
                    />
                );
            })}
        </div>
    );
}
